// @ts-nocheck
/* eslint-disable react/prop-types */
/**
 * CircleNode — Circular icon node for SolyticsFlow.
 *
 * A small round node suitable for graphs, lineage diagrams, etc.
 * The tooltip is fully developer-controlled via data.tooltip (string | JSX).
 *
 * Supports:
 *   data.label       — accessible label (aria-label)
 *   data.icon        — ReactNode rendered inside the circle
 *   data.color       — background colour override (theme tokens supported)
 *   data.borderColor — border override
 *   data.size        — circle diameter in px (default 32)
 *   data.tooltip     — string | ReactNode
 *   data.menuItems   — SolyticsMenus item array
 */
import React, { memo, useMemo } from 'react';
import { Position } from 'reactflow';
import { useTheme } from '@mui/material/styles';
import SolyticsBox from '../../../SolyticsBox';
import NodeHandles from '../shared/NodeHandles';
import NodeTooltip from '../shared/NodeTooltip';
import NodeMenu from '../shared/NodeMenu';
import { resolveThemeColor } from '../shared/themeColorUtils';

const CircleNode = memo(({ id, data, parentId }) => {
  const theme = useTheme();
  const size = data?.size ?? 32;

  const colors = useMemo(
    () => ({
      bg: resolveThemeColor(theme, data?.bgColor ?? data?.color, 'grey.30'),
      border: resolveThemeColor(theme, data?.borderColor, 'divider'),
    }),
    [data, theme]
  );

  const defaultHandles = useMemo(
    () => [
      { type: 'target', position: Position.Left, id: 'target' },
      { type: 'source', position: Position.Right, id: 'source' },
    ],
    []
  );

  return (
    <NodeMenu menuItems={data?.menuItems} nodeId={id} parentId={parentId}>
      <SolyticsBox position="relative" width={size} height={size}>
        <NodeHandles handles={data?.handles} fallbackHandles={defaultHandles} />

        <NodeTooltip tooltip={data?.tooltip}>
          <SolyticsBox
            tabIndex={0}
            role="button"
            aria-label={data?.label ?? 'Node'}
            width={size}
            height={size}
            borderRadius="50%"
            bgcolor={colors.bg}
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              border: `1px solid ${colors.border}`,
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' },
              '&:focus-visible': {
                outline: (t) => `2px solid ${t.palette.primary.main}`,
                outlineOffset: 2,
              },
            }}
          >
            {data?.icon}
          </SolyticsBox>
        </NodeTooltip>
      </SolyticsBox>
    </NodeMenu>
  );
});
CircleNode.displayName = 'CircleNode';

export default CircleNode;
